import { executeGetData } from '../../../services/ApiBase';
import { ConfigUrl } from '../../../auth/OidcConfig';

/** A tenant option for the Mapping tab tenant dropdown. */
export interface TenantOption {
  tenantId: string;
  name: string;
  status?: string;
}

/**
 * Fetch the tenants that have the given trade (read-only).
 *
 * Backend: GET /tenantprovision/getTenantsByTrade/tradeId/{tradeId}
 *   → tenants joined from the provider tenant_trade_map (active maps only).
 *
 * Returns Tenant entities, so the human name is `name` and the id is `tenantId`.
 * Returns [] on any non-OK response so the dropdown degrades gracefully.
 */
export async function getTenantsByTrade(
  tradeId: number,
  accessToken: string,
): Promise<TenantOption[]> {
  const res = await executeGetData(
    `${ConfigUrl.baseURL}/tenantprovision/getTenantsByTrade/tradeId/${tradeId}`,
    accessToken,
  );
  if (!res || !res.ok || !Array.isArray(res.data)) return [];
  return (res.data as Array<Record<string, unknown>>)
    .map(t => ({
      tenantId: String(t.tenantId ?? ''),
      name: String(t.name ?? t.tenantId ?? ''),
      status: t.status != null ? String(t.status) : undefined,
    }))
    .filter(t => t.tenantId !== '');
}

/** One active tenant<->trade mapping row (provider tenant_trade_map). */
export interface TenantTradeMapping {
  tenantId: string;
  tenantName: string;
  tradeId: number;
}

/**
 * Fetch every active tenant<->trade mapping in one Provider-DB query (read-only). Powers the
 * Trade Templates "By tenant" library grouping without any tenant-DB reads.
 *
 * Backend: GET /tenantprovision/getTenantTradeMap → provider tenant_trade_map (active rows only).
 * Returns [] on any non-OK response so the view degrades gracefully.
 */
export async function getTenantTradeMap(accessToken: string): Promise<TenantTradeMapping[]> {
  const res = await executeGetData(`${ConfigUrl.baseURL}/tenantprovision/getTenantTradeMap`, accessToken);
  if (!res || !res.ok || !Array.isArray(res.data)) return [];
  return (res.data as Array<Record<string, unknown>>)
    .map(m => ({
      tenantId: String(m.tenantId ?? ''),
      tenantName: String(m.tenantName ?? m.tenantId ?? ''),
      tradeId: Number(m.tradeId),
    }))
    .filter(m => m.tenantId !== '' && !Number.isNaN(m.tradeId));
}
